import { useEffect, useMemo, useState } from "react";
import { getAdminTransactions, sendTaxReceipt } from "../../api/adminInventory";
import { CDN_BASE_URL, handleImageError, resolveImageUrl } from "../../helper/image";

const PAGE_SIZE = 25;
const TZ = "Australia/Sydney";

function centsToDisplay(cents) {
  return (Number(cents) / 100).toFixed(2);
}

function formatDate(isoString) {
  if (!isoString) return "";
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(isoString));
}

function buyerLabel(user) {
  if (!user) return "Guest / unknown buyer";
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  if (name && user.email) return `${name} · ${user.email}`;
  return name || user.email || user.tiktok_username || "Unknown buyer";
}

function itemImageUrl(item) {
  if (item.product_type === "CARD" || item.set_id) {
    return resolveImageUrl(`${CDN_BASE_URL}/imgs/${item.set_id || item.base_image_url}-low.png`);
  }
  if (item.base_image_url) {
    return resolveImageUrl(`${CDN_BASE_URL}/products/${item.base_image_url}.PNG`);
  }
  return "/DEFAULT.PNG";
}

function statusClass(status) {
  const value = String(status || "").toLowerCase();
  if (value === "approved" || value === "succeeded") return "is-success";
  if (value === "failed") return "is-error";
  if (value === "pending") return "is-pending";
  return "";
}

export default function TransactionHistory() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: PAGE_SIZE });
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [receiptEmail, setReceiptEmail] = useState("");
  const [receiptStatus, setReceiptStatus] = useState(null);
  const [sendingReceipt, setSendingReceipt] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    (async () => {
      try {
        const response = await getAdminTransactions({ page, pageSize: PAGE_SIZE });
        if (!active) return;
        setData({
          items: response.items || [],
          total: response.total || 0,
          page: response.page || page,
          page_size: response.page_size || PAGE_SIZE,
        });
        setSelectedId(null);
        setReceiptEmail("");
        setReceiptStatus(null);
      } catch (err) {
        if (!active) return;
        setError(err.message || "Failed to load transactions");
        setData({ items: [], total: 0, page, page_size: PAGE_SIZE });
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [page]);

  const selected = useMemo(
    () => data.items.find((row) => row.transaction.id === selectedId) || null,
    [data.items, selectedId]
  );

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || PAGE_SIZE)));

  if (loading) {
    return <p className="admin-empty">Loading transactions…</p>;
  }

  if (error) {
    return <p className="admin-message error">{error}</p>;
  }

  if (!data.items.length) {
    return <p className="admin-empty">No production transactions found.</p>;
  }

  async function handleSendTaxReceipt(tx) {
    const recipient = receiptEmail.trim() || tx.user?.email || "";
    if (!recipient) {
      setReceiptStatus({ type: "error", text: "Enter an email address" });
      return;
    }
    setSendingReceipt(true);
    setReceiptStatus(null);
    try {
      const result = await sendTaxReceipt({
        email: recipient,
        customerName: [tx.user?.first_name, tx.user?.last_name].filter(Boolean).join(" "),
        transactionId: tx.id,
        items: [],
      });
      setReceiptStatus({ type: "success", text: result.detail });
    } catch (err) {
      setReceiptStatus({ type: "error", text: err.message });
    } finally {
      setSendingReceipt(false);
    }
  }

  if (selected) {
    const tx = selected.transaction;
    return (
      <div className="admin-tx-detail">
        <button
          type="button"
          className="admin-action-btn secondary"
          onClick={() => setSelectedId(null)}
        >
          ← Back to transactions
        </button>

        <section className="admin-section">
          <div className="admin-tx-header">
            <h3>Order #{tx.id}</h3>
            <span className={`admin-tx-status ${statusClass(tx.status)}`}>{tx.status}</span>
          </div>
          <dl className="admin-tx-meta">
            <dt>Buyer</dt>
            <dd>{buyerLabel(tx.user)}</dd>
            {tx.user?.tiktok_username ? (
              <>
                <dt>TikTok</dt>
                <dd>@{tx.user.tiktok_username}</dd>
              </>
            ) : null}
            <dt>Created</dt>
            <dd>{formatDate(tx.created_at)}</dd>
            <dt>Total</dt>
            <dd>${centsToDisplay(tx.total_price)}</dd>
            <dt>Payment</dt>
            <dd>{tx.payment_method_id || "—"}</dd>
            <dt>Shipping</dt>
            <dd>
              {tx.shipping_method || "—"}
              {tx.shipped ? " · shipped" : " · not shipped"}
            </dd>
            {tx.track_number ? (
              <>
                <dt>Tracking</dt>
                <dd>{tx.track_number}</dd>
              </>
            ) : null}
          </dl>
        </section>

        <section className="admin-section">
          <h3>Email tax invoice</h3>
          <div className="admin-form-grid">
            <label className="full-width">
              Email
              <input
                type="email"
                value={receiptEmail}
                onChange={(e) => setReceiptEmail(e.target.value)}
                placeholder="customer@email.com"
              />
            </label>
          </div>
          {receiptStatus && (
            <p className={`admin-message ${receiptStatus.type}`}>{receiptStatus.text}</p>
          )}
          <button
            type="button"
            className="admin-action-btn"
            disabled={sendingReceipt}
            onClick={() => handleSendTaxReceipt(tx)}
          >
            {sendingReceipt ? "Sending…" : "Email tax invoice"}
          </button>
        </section>

        <section className="admin-section">
          <h3>Items ({selected.items.length})</h3>
          <ul className="admin-tx-items">
            {selected.items.map((item) => (
              <li key={item.id}>
                <img
                  src={itemImageUrl(item)}
                  alt=""
                  onError={handleImageError}
                />
                <div>
                  <strong>{item.product_name}</strong>
                  <small>
                    {[item.condition, item.variant].filter(Boolean).join(" · ") || "—"}
                    {" · "}
                    qty {item.quantity}
                    {" · "}
                    ${centsToDisplay(item.sold_price)}
                  </small>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    );
  }

  return (
    <div className="admin-tx-list">
      <p className="admin-subtitle">
        Showing page {data.page} of {totalPages} · {data.total} production transaction
        {data.total === 1 ? "" : "s"}
      </p>

      <ul className="admin-tx-rows">
        {data.items.map(({ transaction, items }) => (
          <li key={transaction.id}>
            <button
              type="button"
              className="admin-tx-row"
              onClick={() => {
                setSelectedId(transaction.id);
                setReceiptEmail(transaction.user?.email || "");
                setReceiptStatus(null);
              }}
            >
              <div className="admin-tx-row-main">
                <strong>#{transaction.id}</strong>
                <span className={`admin-tx-status ${statusClass(transaction.status)}`}>
                  {transaction.status}
                </span>
              </div>
              <small>{buyerLabel(transaction.user)}</small>
              <small>
                {formatDate(transaction.created_at)} · {items.length} item
                {items.length === 1 ? "" : "s"} · ${centsToDisplay(transaction.total_price)}
              </small>
              <div className="admin-tx-thumbs">
                {items.slice(0, 5).map((item) => (
                  <img
                    key={item.id}
                    src={itemImageUrl(item)}
                    alt=""
                    onError={handleImageError}
                  />
                ))}
              </div>
            </button>
          </li>
        ))}
      </ul>

      <div className="admin-tx-pagination">
        <button
          type="button"
          className="admin-action-btn secondary"
          disabled={page <= 1}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
        >
          Previous
        </button>
        <span>
          Page {page} / {totalPages}
        </span>
        <button
          type="button"
          className="admin-action-btn secondary"
          disabled={page >= totalPages}
          onClick={() => setPage((current) => current + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
